
<template>
    <div>
      <!--/*  ナビゲーションなどのヘッダー  */-->
      <CommonHeader ref="commonHeader" />

      <transition-group name="tran-nortification">
        <div v-for="nortice in nortification.list" :key="nortice.message" :class="'header-nortification-' + nortice.type" class="header-nortification">
          <span style="margin-left: auto;">
            {{nortice.message}}
          </span>
          <span style="margin-left: auto;font-size: xx-large;padding: 0;padding: 0 10px;cursor: pointer;line-height: 0;" @click="nortification.clear(nortice.identifer)">
            ×
          </span>
        </div>
      </transition-group>

      <!--/*  ページごとに変わるところ */-->
      <main class="main-contents" :style="{'min-height':mainAreaMinHeight}">
        <router-view v-slot="{ Component }">
          <!--/* メインページ（Home）は読込結果を残したいのでkeep-aliveする */-->
          <keep-alive>
            <component :is="Component" :key="$route.fullPath" v-if="isHomeURL" />
          </keep-alive>
          <!--/* その他のページは unmount, beforeUnmountをコールして画面移動時にクリアしたいのでkeep-alive外で表示 */-->
          <component :is="Component" v-if="!isHomeURL" />
        </router-view>
      </main>

      <MobileNav />

      <footer v-if="isFooterVisible" :style="{height:footerHeight + 'px'}">
        <span style="color: #6c757d">
          Copyright © 2022-2025 forkstyle All Rights Reserved. <span class="version">{{displayVersion}}</span>
        </span>
      </footer>

      <!--/*  デバッグ用のリンク集  */-->
      <component v-if="isDebug" :is="'DebugFooter'" />

      <LoginDialog ref="loginDialog" />
      <LogoutDialog ref="logoutDialog" />  
      <ConfirmDialog ref="confirmDialogRef" :yesButton="confirmYesText">
        <div style="display: flex;justify-content: center;padding:1rem"><span>{{ confirmText }}</span></div>
      </ConfirmDialog>
      <AlertDialog ref="alertDialogRef">
        <div style="display: flex;justify-content: center;padding:1rem"><span>{{ alertText }}</span></div>
      </AlertDialog>
    </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import {  Ref } from "vue-property-decorator";
import CommonHeader from "./component/CommonHeader.vue";
import MobileNav from "./component/MobileNav.vue";

import LoginDialog from "@/component/dialog/LoginDialog.vue";
import LogoutDialog from "@/component/dialog/LogoutDialog.vue";
import ConfirmDialog from "@/component/dialog/ConfirmDialog.vue";
import AlertDialog from "@/component/dialog/AlertDialog.vue";


import rest from "@/rest/restAccess";

import account from "@/state/AccountState";
import nortification from "@/state/NortificationState";


import common from "@/common";
import constVariable from "@/const";
import router from '@/router';
import routeState from './state/RouteState';
import headerFooterHeight from './headerFooterHeight';

const FOOTER_HEIGHT = 36


@Options({
  components: {
    CommonHeader,
    LogoutDialog,
    LoginDialog,
    ConfirmDialog,
    AlertDialog,
    MobileNav,
  },
})
export default class extends Vue {
  
  //mounted前のlifesycleだと初期化されていないので使用できない点に注意
  //------------------------------------------
  @Ref() public readonly commonHeader = null as any as CommonHeader;
  @Ref() public readonly logoutDialog = null as any as LogoutDialog;
  @Ref() public readonly loginDialog = null as any as LoginDialog;
  @Ref() private readonly confirmDialogRef = null as any as ConfirmDialog;
  @Ref() private readonly alertDialogRef = null as any as AlertDialog;
  //------------------------------------------

  confirmText = "";
  confirmYesText = "";

  public confirmDialog = {
    open: (confirmText:string, confirmYesText?:string) :Promise<boolean>=> {
      this.confirmText = confirmText;
      this.confirmYesText = confirmYesText ? confirmText: "OK";
      return this.confirmDialogRef.openDialog()
    },
    close: () => {
      return this.confirmDialogRef.closeDialog()
    }
  }

  alertText = "";
  public alertDialog = {
    open: (alertText:string) :Promise<undefined>=> {
      this.alertText = alertText;
      return this.alertDialogRef.openDialog()
    },
    close: () => {
      return this.alertDialogRef.closeDialog()
    }
  }



  account = account;
  nortification = nortification;
  isFooterVisible = true;


  get isHomeURL(){
    return this.$route.fullPath==='/'
  }

  get processEnv(): any {
    return process.env;
  }

  get messages() {
    return window.messages;
  }

  get restAccess() {
    return rest;
  }

  get const() {
    return constVariable;
  }

  get common() {
    return common;
  }

  get isDebug(){
    return process.env.VUE_APP_MODE === "development"
  }

  get displayVersion(): any {
    return process.env.VUE_APP_DISPLAY_VERSION;
  }

  get mainAreaMinHeight(){
    return 'calc(100vh - ' + headerFooterHeight.getHeight() +  'px - ' + (nortification.list.length * 40) + 'px)'
  }

  get footerHeight(){
    return FOOTER_HEIGHT;
  }

  get contactEmailAddress(){
    return "contact".toString() + String.fromCharCode(64) + "forkstyle".toString() + String.fromCharCode(46) + "com".toString()
  }
  

  get isMobile() {
    //mobileとPCの切り替え確認には、ChromeのDebugツールでモードを切り替えた後、ブラウザの再読込が必要。変更を自動検知する変数が含まれないため。
    const a = navigator.userAgent || navigator.vendor || (window as any).opera;
    return (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
        a.substr(0, 4)
      )
    );
  }

  public routerPushOnLink(e:MouseEvent, url:string){
    //ctrlを押している時は、そのままブラウザの機能を使ってaタグを別タブで開くとしたいので何もしない
    if(!e.ctrlKey){
      e.preventDefault();
      this.$router.push(url);
    }
  }

  public focus(elementId:string){
    this.$nextTick(()=>{
      document.getElementById(elementId)?.focus()
    })
  }
  public hideFooter(){
    this.isFooterVisible = false;
  }
  public showFooter(){
    this.isFooterVisible = true;
  }
  nortifyAccountChangeToBrowserTabs() {
    localStorage.setItem("AC", Math.random().toString());
  }


  init() {
    this.account.reload();
    
    //これが常にtrueという前提でロジックを組むので、念の為Assert処理を入れておく
    if(router !== this.$router) throw "error";

    //storageの通知機能を使って、ブラウザの別タブでのログイン・ログアウトを検知する
    window.addEventListener("storage", (event) => {
      if (event.key === "AC") {
        if (process.env.VUE_APP_MODE === "development") {
          console.log("アカウント情報が他のブラウザタブより更新されました。", event);
        }
        this.account.reload();
      }
    });
    window.addEventListener('error', (event) => {
        console.error("window error:", event)
        const msg = event ? event.error : event
        rest.postStatistics("window-error", msg)
    });

    headerFooterHeight.regesterEvent();
  }

  created() {
    routeState.rootInstance = this
    this.init();

    window.onbeforeunload = () => {
      //ホーム画面でリロードした時にスクロールを一番上に移動させる
      if(this.isHomeURL){
        window.scrollTo(0, 0);
      }
    }
  }
}
</script>

<style lang="scss" scoped>

  footer {
    padding-bottom: 6px;
    padding-top: 6px;
    justify-content: center;
    display: flex;
    overflow: hidden;
    white-space: nowrap;
    word-break: keep-all;
    .version{
      font-size:small;
      padding-left:28px;
    }
    
    @media (max-width: 680px) {
      font-size: 0.95rem;
      .version{
        padding-left:24px;
      }
    }
    @media (max-width: 580px) {
      font-size: 0.9rem;
      .version{
        padding-left:20px;
      }
    }
    @media (max-width: 480px) {
      font-size: 0.8rem;
      .version{
        padding-left:16px;
      }
    }
    @media (max-width: 380px) {
      font-size: 0.7rem;
      .version{
        padding-left:10px;
      }
    }
  }
  
  .header-nortification{
    min-height: 40px;
    box-sizing: border-box;
    overflow-y: hidden;
    display: flex;
    word-break: break-all;
    padding: 4px;
    align-items: center;
    font-family:initial;/* xの縦の位置がずれるのでfontを指定しない */
    font-size: large;
  }
  
  .header-nortification-info{
    background-color: #99F;
    color: #FFF;
  }
  .header-nortification-success{
    background-color: #073;
    color: #FFF;
  }
  .header-nortification-warn{
    background-color: #BB4;
    color: #FFF;
  }
  .header-nortification-error{
    background-color: #933;
    color: #FFF;
  }
  
  
  .tran-nortification-enter-active, .tran-nortification-leave-active {
    transition: all 0.2s;
  }
  .tran-nortification-enter-to {
    opacity: 1;
    max-height: 30px;
    padding: 8px;
  }
  .tran-nortification-enter, .tran-nortification-leave-to {
    opacity: 0;
    max-height: 0;
    padding:0;
  }
</style>